import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { usePermissions } from "./hooks/usePermissions";
import { useTranslation } from "./i18n";
import AppLayout from "./components/layout/AppLayout/AppLayout";
import { AccessControlledRoute } from "./components/common/AccessControlledRoute";
import { PermissionRoute } from "./components/common/PermissionRoute";
import FeatureAwareHome from "./pages/FeatureAwareHome";
import Home from "./pages/Home";
import LoginPage from "./pages/Login";
import LoginLegacyPage from "./pages/LoginLegacy";
import Profile from "./pages/Profile";
import Statistics from "./pages/Statistics";
import History from "./pages/History";
import OrderManagement from "./pages/OrderManagement";
import ReviewExport from "./pages/ReviewExport";
import AnnotationManagement from "./pages/AnnotationManagement";
import ManualRecognition from "./pages/ManualRecognition";
import DeviceMosquito from "./pages/DeviceMosquito.tsx";
import MpMap from "./client_uniapp/map/MpMap";
import DeviceManagement from "./pages/DeviceManagement.tsx";
import DeviceUpgradeManagement from "./pages/admin/DeviceUpgradeManagement.tsx";
import PackageManagementV1 from "./pages/admin/PackageManagementV1.tsx";
import ModelManagement from "./pages/admin/ModelManagement.tsx";
import ModelEvaluationReportPage from "./pages/admin/ModelEvaluationReportPage.tsx";
import ModelEvaluationWorkspacePage from "./pages/admin/ModelEvaluationWorkspacePage.tsx";
import ProjectManagementV1 from "./pages/admin/ProjectManagementV1.tsx";
import ProjectViewerScopes from "./pages/admin/ProjectViewerScopes.tsx";
import OperationLog from "./pages/admin/OperationLog.tsx";
import SystemSettings from "./pages/admin/SystemSettings.tsx";
import UserManagement from "./pages/admin/UserManagement.tsx";
import PaymentMerchantManagement from "./pages/admin/PaymentMerchantManagement.tsx";
import RoleManagement from "./pages/admin/RoleManagement.tsx";
import RegionManagement from "./pages/admin/RegionManagement.tsx";
import { POST_LOGIN_REDIRECT_STORAGE_KEY } from "./utils/axiosConfig";
import {
  DEVICE_ACCESS_PERMISSIONS,
  DEVICE_DISTRIBUTION_ACCESS_PERMISSIONS,
  HISTORY_ACCESS_PERMISSIONS,
  MENDER_OTA_CONFIG_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ARTIFACT_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BATCH_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BINDING_READ_ACCESS_PERMISSIONS,
  PAYMENT_ACCESS_PERMISSIONS,
  PAYMENT_MERCHANT_MANAGEMENT_ACCESS_PERMISSIONS,
  OPERATION_LOG_ACCESS_PERMISSIONS,
  REGION_MANAGEMENT_ACCESS_PERMISSIONS,
  ROLE_MANAGEMENT_ACCESS_PERMISSIONS,
  STATISTICS_ACCESS_PERMISSIONS,
  STATISTICS_SPECIES_ANALYSIS_ACCESS_PERMISSIONS,
  STATISTICS_TREND_ANALYSIS_ACCESS_PERMISSIONS,
  SYSTEM_SETTINGS_ACCESS_PERMISSIONS,
} from "./utils/permissions";

import "./App.css";

const rememberPostLoginRedirect = (location: {
  pathname: string;
  search: string;
  hash: string;
}) => {
  const redirectTarget = `${location.pathname}${location.search}${location.hash}`;

  if (redirectTarget && redirectTarget !== "/login") {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, redirectTarget);
  }
};

const AuthProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { authStatus } = useAuth();
  const { t } = useTranslation("common");

  if (authStatus === "checking") {
    return <div>{t("messages.loading")}</div>;
  }

  if (authStatus !== "authenticated") {
    rememberPostLoginRedirect(location);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

interface HomeFallbackAccess {
  canAccessSpeciesAnalysis: boolean;
  canAccessTrendAnalysis: boolean;
  canAccessDeviceDistribution: boolean;
  canAccessHistory: boolean;
  canAccessDeviceManagement: boolean;
  canReadDeviceUpgradeBindings: boolean;
  canReadDeviceUpgradeArtifacts: boolean;
  canReadDeviceUpgradeBatches: boolean;
  canAccessMenderOtaConfig: boolean;
  canAccessAnnotationManagement: boolean;
  canAccessManualRecognitionPage: boolean;
  canAccessReviewExport: boolean;
  canAccessProjectManagement: boolean;
  canAccessProjectViewerManagement: boolean;
  canAccessPackageManagement: boolean;
  canAccessModelManagement: boolean;
  canAccessModelValidation: boolean;
  canAccessOrderManagement: boolean;
  canAccessPaymentMerchantManagement: boolean;
  canAccessUserManagement: boolean;
  canAccessRoleManagement: boolean;
  canAccessRegionManagement: boolean;
  canAccessSystemSettings: boolean;
  canAccessOperationLogs: boolean;
}

const getStatisticsEntryPath = (access: {
  canAccessSpeciesAnalysis: boolean;
  canAccessTrendAnalysis: boolean;
}): string => {
  if (access.canAccessSpeciesAnalysis) {
    return "/statistics/species-analysis";
  }
  if (access.canAccessTrendAnalysis) {
    return "/statistics/trend-analysis";
  }
  return "/profile";
};

const getHomeFallbackPath = (access: HomeFallbackAccess): string => {
  if (access.canAccessSpeciesAnalysis || access.canAccessTrendAnalysis) {
    return getStatisticsEntryPath(access);
  }
  if (access.canAccessDeviceDistribution) {
    return "/device-mosquito";
  }
  if (access.canAccessHistory) {
    return "/history";
  }
  if (access.canAccessDeviceManagement) {
    return "/device-management";
  }
  if (access.canReadDeviceUpgradeBindings) {
    return "/admin/operations/device-upgrade-bindings";
  }
  if (access.canReadDeviceUpgradeArtifacts) {
    return "/admin/operations/device-upgrade-artifacts";
  }
  if (access.canReadDeviceUpgradeBatches) {
    return "/admin/operations/device-upgrade-batches";
  }
  if (access.canAccessMenderOtaConfig) {
    return "/admin/system-settings?section=menderOta";
  }
  if (access.canAccessAnnotationManagement) {
    return "/annotation-management";
  }
  if (access.canAccessManualRecognitionPage) {
    return "/manual-recognition";
  }
  if (access.canAccessReviewExport) {
    return "/review-export";
  }
  if (access.canAccessProjectManagement) {
    return "/admin/project-management-v1";
  }
  if (access.canAccessProjectViewerManagement) {
    return "/admin/project-management-v1/viewers";
  }
  if (access.canAccessPackageManagement) {
    return "/admin/package-management";
  }
  if (access.canAccessModelManagement) {
    return "/admin/model-management/upload";
  }
  if (access.canAccessModelValidation) {
    return "/admin/model-management/evaluations";
  }
  if (access.canAccessOrderManagement) {
    return "/orders";
  }
  if (access.canAccessPaymentMerchantManagement) {
    return "/admin/payment-merchants";
  }
  if (access.canAccessUserManagement) {
    return "/admin/users";
  }
  if (access.canAccessRoleManagement) {
    return "/admin/roles";
  }
  if (access.canAccessRegionManagement) {
    return "/admin/regions";
  }
  if (access.canAccessSystemSettings) {
    return "/admin/system-settings";
  }
  if (access.canAccessOperationLogs) {
    return "/admin/operation-logs";
  }
  return "/profile";
};

const App: React.FC = () => {
  const location = useLocation();
  const {
    canAccessAnnotationManagement,
    canAccessManualRecognitionPage,
    canAccessDeviceDistribution,
    canAccessDeviceManagement,
    canReadDeviceUpgradeBindings,
    canReadDeviceUpgradeArtifacts,
    canReadDeviceUpgradeBatches,
    canAccessMenderOtaConfig,
    canAccessHistory,
    canAccessHome,
    canAccessHomeDashboard,
    canAccessOperationLogs,
    canAccessOrderManagement,
    canAccessPackageManagement,
    canAccessModelManagement,
    canAccessModelValidation,
    canAccessPaymentMerchantManagement,
    canAccessProjectManagement,
    canAccessProjectViewerManagement,
    canAccessRegionManagement,
    canAccessReviewExport,
    canAccessRoleManagement,
    canAccessSpeciesAnalysis,
    canAccessTrendAnalysis,
    canAccessSystemSettings,
    canAccessUserManagement,
  } = usePermissions();
  const { t } = useTranslation(["history", "common", "admin"]);
  const homeFallbackPath = getHomeFallbackPath({
    canAccessSpeciesAnalysis,
    canAccessTrendAnalysis,
    canAccessDeviceDistribution,
    canAccessHistory,
    canAccessDeviceManagement,
    canReadDeviceUpgradeBindings,
    canReadDeviceUpgradeArtifacts,
    canReadDeviceUpgradeBatches,
    canAccessMenderOtaConfig,
    canAccessAnnotationManagement,
    canAccessManualRecognitionPage,
    canAccessReviewExport,
    canAccessProjectManagement,
    canAccessProjectViewerManagement,
    canAccessPackageManagement,
    canAccessModelManagement,
    canAccessModelValidation,
    canAccessOrderManagement,
    canAccessPaymentMerchantManagement,
    canAccessUserManagement,
    canAccessRoleManagement,
    canAccessRegionManagement,
    canAccessSystemSettings,
    canAccessOperationLogs,
  });

  if (location.pathname === "/login") {
    return <LoginPage />;
  }

  if (location.pathname === "/login-legacy") {
    return <LoginLegacyPage />;
  }

  if (location.pathname === "/mp-map") {
    return <MpMap />;
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route
          path="/"
          element={
            <AuthProtectedRoute>
              {canAccessHome ? (
                canAccessHomeDashboard ? (
                  <FeatureAwareHome />
                ) : (
                  <Home />
                )
              ) : (
                <Navigate to={homeFallbackPath} replace />
              )}
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <AuthProtectedRoute>
              <Profile />
            </AuthProtectedRoute>
          }
        />
        <Route path="/image-detection" element={<Navigate to="/" replace />} />
        <Route
          path="/statistics"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={STATISTICS_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.statistics.title")}
                accessDeniedDescription={t("common:accessDenied.statistics.description")}
              >
                <Navigate
                  to={getStatisticsEntryPath({
                    canAccessSpeciesAnalysis,
                    canAccessTrendAnalysis,
                  })}
                  replace
                />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/statistics/species-analysis"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={STATISTICS_SPECIES_ANALYSIS_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.statistics.title")}
                accessDeniedDescription={t("common:accessDenied.statistics.description")}
              >
                <Statistics key="species-analysis" lockedChartType="distribution" />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/statistics/trend-analysis"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={STATISTICS_TREND_ANALYSIS_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.statistics.title")}
                accessDeniedDescription={t("common:accessDenied.statistics.description")}
              >
                <Statistics key="trend-analysis" lockedChartType="trend" />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/annotation-management"
          element={
            <AuthProtectedRoute>
              <AccessControlledRoute
                canAccess={canAccessAnnotationManagement}
                accessDeniedMessage={t("common:featureNotAvailable")}
                accessDeniedDescription={t(
                  "history:messages.annotationManagementAccessDenied",
                )}
                accessDeniedActionText={t("common:backToHome")}
              >
                <AnnotationManagement />
              </AccessControlledRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/manual-recognition"
          element={
            <AuthProtectedRoute>
              <AccessControlledRoute
                canAccess={canAccessManualRecognitionPage}
                accessDeniedMessage={t("common:featureNotAvailable")}
                accessDeniedDescription={t(
                  "history:messages.manualRecognitionAccessDenied",
                )}
                accessDeniedActionText={t("common:backToHome")}
              >
                <ManualRecognition />
              </AccessControlledRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={PAYMENT_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.orders.title")}
                accessDeniedDescription={t("common:accessDenied.orders.description")}
              >
                <OrderManagement />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/__prototype/payment-order-export"
          element={<OrderManagement prototypeMode />}
        />
        <Route
          path="/history"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={HISTORY_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.history.title")}
                accessDeniedDescription={t("common:accessDenied.history.description")}
              >
                <History />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/review-export"
          element={
            <AuthProtectedRoute>
              <AccessControlledRoute
                canAccess={canAccessReviewExport}
                accessDeniedMessage={t("common:featureNotAvailable")}
                accessDeniedDescription={t(
                  "history:messages.reviewExportAccessDenied",
                )}
                accessDeniedActionText={t("common:backToHome")}
              >
                <ReviewExport />
              </AccessControlledRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/device-mosquito"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={DEVICE_DISTRIBUTION_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.deviceDistribution.title")}
                accessDeniedDescription={t("common:accessDenied.deviceDistribution.description")}
              >
                <DeviceMosquito />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/stat-mosquito"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={STATISTICS_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.statistics.title")}
                accessDeniedDescription={t("common:accessDenied.statistics.description")}
              >
                <Navigate
                  to={getStatisticsEntryPath({
                    canAccessSpeciesAnalysis,
                    canAccessTrendAnalysis,
                  })}
                  replace
                />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/device-management"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={DEVICE_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.deviceManagement.title")}
                accessDeniedDescription={t("common:accessDenied.deviceManagement.description")}
              >
                <DeviceManagement />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/operations/mender-ota"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={MENDER_OTA_CONFIG_READ_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:featureNotAvailable")}
                accessDeniedDescription={t("common:accessDenied.systemSettings.description")}
              >
                <Navigate to="/admin/system-settings?section=menderOta" replace />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/operations/device-upgrade-bindings"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={OPERATIONS_DEVICE_UPGRADE_BINDING_READ_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.deviceUpgrades.title")}
                accessDeniedDescription={t("common:accessDenied.deviceUpgrades.description")}
              >
                <DeviceUpgradeManagement key="device-upgrade-bindings" section="bindings" />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/operations/device-upgrade-artifacts"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={OPERATIONS_DEVICE_UPGRADE_ARTIFACT_READ_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.deviceUpgrades.title")}
                accessDeniedDescription={t("common:accessDenied.deviceUpgrades.description")}
              >
                <DeviceUpgradeManagement key="device-upgrade-artifacts" section="artifacts" />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/operations/device-upgrade-batches"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={OPERATIONS_DEVICE_UPGRADE_BATCH_READ_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.deviceUpgrades.title")}
                accessDeniedDescription={t("common:accessDenied.deviceUpgrades.description")}
              >
                <DeviceUpgradeManagement key="device-upgrade-batches" section="batches" />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/project-management-v1"
          element={
            <AuthProtectedRoute>
              <AccessControlledRoute
                canAccess={canAccessProjectManagement}
                accessDeniedMessage={t("common:accessDenied.projectManagement.title")}
                accessDeniedDescription={t("common:accessDenied.projectManagement.description")}
                accessDeniedActionText={t("common:backToHome")}
              >
                <ProjectManagementV1 />
              </AccessControlledRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/project-management-v1/viewers"
          element={
            <AuthProtectedRoute>
              <AccessControlledRoute
                canAccess={canAccessProjectViewerManagement}
                accessDeniedMessage={t("common:accessDenied.projectManagement.title")}
                accessDeniedDescription={t("common:accessDenied.projectManagement.description")}
                accessDeniedActionText={t("common:backToHome")}
              >
                <ProjectViewerScopes />
              </AccessControlledRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/package-management"
          element={
            <AuthProtectedRoute>
              <PackageManagementV1 />
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/model-management"
          element={
            <Navigate
              to={
                canAccessModelManagement
                  ? "/admin/model-management/upload"
                  : "/admin/model-management/evaluations"
              }
              replace
            />
          }
        />
        {(
          [
            ["upload", "upload"],
            ["configuration", "configuration"],
            ["types", "types"],
          ] as const
        ).map(([path, mode]) => (
          <Route
            key={path}
            path={`/admin/model-management/${path}`}
            element={
              <AuthProtectedRoute>
                <AccessControlledRoute
                  canAccess={canAccessModelManagement}
                  accessDeniedMessage={t(
                    "common:accessDenied.modelManagement.title",
                    { defaultValue: "模型管理不可用" },
                  )}
                  accessDeniedDescription={t(
                    "common:accessDenied.modelManagement.description",
                    { defaultValue: "您没有权限查看模型管理。" },
                  )}
                >
                  <ModelManagement mode={mode} />
                </AccessControlledRoute>
              </AuthProtectedRoute>
            }
          />
        ))}
        {[
          "validation-datasets",
          "validation-rules",
          "evaluations",
        ].map((path) => (
          <Route
            key={path}
            path={`/admin/model-management/${path}`}
            element={
              <AuthProtectedRoute>
                <AccessControlledRoute
                  canAccess={canAccessModelValidation}
                  accessDeniedMessage={t(
                    "common:accessDenied.modelManagement.title",
                  )}
                  accessDeniedDescription={t(
                    "common:accessDenied.modelManagement.description",
                  )}
                >
                  <ModelEvaluationWorkspacePage />
                </AccessControlledRoute>
              </AuthProtectedRoute>
            }
          />
        ))}
        <Route
          path="/admin/model-management/evaluations/:taskId"
          element={
            <AuthProtectedRoute>
              <AccessControlledRoute
                canAccess={canAccessModelValidation}
                accessDeniedMessage={t(
                  "common:accessDenied.modelManagement.title",
                )}
                accessDeniedDescription={t(
                  "common:accessDenied.modelManagement.description",
                )}
              >
                <ModelEvaluationReportPage />
              </AccessControlledRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={["user:read"]}
                accessDeniedMessage={t("common:accessDenied.userManagement.title")}
                accessDeniedDescription={t("common:accessDenied.userManagement.description")}
              >
                <UserManagement />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/system-settings"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={SYSTEM_SETTINGS_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage="System Settings Unavailable"
                accessDeniedDescription="You do not have permission to view system settings."
              >
                <SystemSettings />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/operation-logs"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={OPERATION_LOG_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.operationLog.title", {
                  defaultValue: "操作日志不可用",
                })}
                accessDeniedDescription={t(
                  "common:accessDenied.operationLog.description",
                  {
                    defaultValue: "您没有权限查看操作日志。",
                  },
                )}
              >
                <OperationLog />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/payment-merchants"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={PAYMENT_MERCHANT_MANAGEMENT_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.paymentMerchantManagement.title")}
                accessDeniedDescription={t("common:accessDenied.paymentMerchantManagement.description")}
              >
                <PaymentMerchantManagement />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={ROLE_MANAGEMENT_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.roleManagement.title")}
                accessDeniedDescription={t("common:accessDenied.roleManagement.description")}
              >
                <RoleManagement />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
        <Route
          path="/admin/regions"
          element={
            <AuthProtectedRoute>
              <PermissionRoute
                permissions={REGION_MANAGEMENT_ACCESS_PERMISSIONS}
                requireAny={true}
                accessDeniedMessage={t("common:accessDenied.regionManagement.title")}
                accessDeniedDescription={t("common:accessDenied.regionManagement.description")}
              >
                <RegionManagement />
              </PermissionRoute>
            </AuthProtectedRoute>
          }
        />
      </Routes>
    </AppLayout>
  );
};

export default App;
